import { recordedGpsEventPayloads } from "../utils/gps-event-actions.js";
//#region ../src/state/replay-engine.ts
/** Default maximum delay between actions in ms (30 seconds real-time) */
const DEFAULT_MAX_DELAY_MS = 3e4;
/** Max consecutive dispatch errors before auto-pause (Risk R7) */
const MAX_CONSECUTIVE_ERRORS = 10;
/** A GPS event payload's time: `rawGpsPoint.timestamp` (new format) or
*  `gpsPoint.timestamp` (old recordings); null when neither is a number. */
function gpsEventTimestamp(payload) {
	const rawGpsPoint = payload.rawGpsPoint;
	if (rawGpsPoint && typeof rawGpsPoint.timestamp === "number") return rawGpsPoint.timestamp;
	const gpsPoint = payload.gpsPoint;
	if (gpsPoint && typeof gpsPoint.timestamp === "number") return gpsPoint.timestamp;
	return null;
}
/**
* Extract an absolute epoch-ms timestamp from a Redux action, or null
* if the action type doesn't carry one.
*
* IMPORTANT: the high-frequency sensor streams (depthSample, qrDetected) return
* null here ON PURPOSE — they are NOT used to pace replay delays (they anchor to
* the GPS/image/session timeline by recorded ORDER instead). Their payload
* `timestamp` IS epoch ms (`performance.timeOrigin + frameTs`, see
* `ar/depth-sampler.ts`); the QR size as-of join reads those payload timestamps
* directly, independently of this pacing function. (Risk R4)
*
* @param action - A Redux action with type and optional payload
* @returns Epoch milliseconds, or null if unavailable/unreliable
*/
function extractActionTimestamp(action) {
	if (!action.payload || typeof action.payload !== "object") return null;
	const payload = action.payload;
	switch (action.type) {
		case "gpsData/recordGpsEvent": return gpsEventTimestamp(payload);
		case "gpsData/recordGpsEventBatch":
			for (const event of recordedGpsEventPayloads(action)) {
				if (typeof event !== "object" || event === null) continue;
				const ts = gpsEventTimestamp(event);
				if (ts !== null && Number.isFinite(ts)) return ts;
			}
			return null;
		case "recording/startSession":
			if (typeof payload.startTime === "number") return payload.startTime;
			return null;
		case "gpsData/markReferencePoint": {
			if (typeof payload.timestamp === "number") return payload.timestamp;
			const rawGpsPoint = payload.rawGpsPoint;
			if (rawGpsPoint && typeof rawGpsPoint.timestamp === "number") return rawGpsPoint.timestamp;
			const gpsPoint = payload.gpsPoint;
			if (gpsPoint && typeof gpsPoint.timestamp === "number") return gpsPoint.timestamp;
			return null;
		}
		case "recording/recordDepthSample": return null;
		case "qrDetected/recordQrDetection": return null;
		case "diagnostics/note": return typeof payload.atMs === "number" ? payload.atMs : null;
		case "recording/endSession": return null;
		default: return null;
	}
}
/**
* Compute the delay in ms between two consecutive actions for replay.
*
* @param currentTs - Epoch ms of the current (just-dispatched) action, or null
* @param nextTs - Epoch ms of the next action, or null
* @param speedFactor - Playback speed multiplier (1 = real-time)
* @param maxDelay - Maximum delay in ms (default: DEFAULT_MAX_DELAY_MS)
* @returns Delay in ms, clamped to [0, maxDelay]
*/
function computeInterActionDelay(currentTs, nextTs, speedFactor, maxDelay = DEFAULT_MAX_DELAY_MS) {
	if (currentTs === null || nextTs === null) return 0;
	const rawDelay = (nextTs - currentTs) / speedFactor;
	return Math.min(Math.max(0, rawDelay), maxDelay);
}
/**
* Async controller that dispatches recorded actions with timed delays.
*
* Uses AbortController for pause/cancel:
* - play() starts the async loop
* - pause() aborts the current controller → loop exits
* - resume() creates a new controller and restarts from current index
* - setSpeed() updates a closure variable, picked up on next delay calc
* - dispose() stops everything and resets state
*/
var ReplayEngine = class {
	state = "idle";
	currentIndex = 0;
	actions = [];
	store = null;
	speedFactor = 1;
	abortController = null;
	progressCallbacks = [];
	completeCallbacks = [];
	errorCallbacks = [];
	currentPlayPromise = null;
	/** Get the current engine state */
	getState() {
		return this.state;
	}
	/** Get the current action index (1-based after dispatch, 0 before play) */
	getCurrentActionIndex() {
		return this.currentIndex;
	}
	/** Register a progress callback: (currentIndex, totalCount) */
	onProgress(callback) {
		this.progressCallbacks.push(callback);
	}
	/** Register a completion callback */
	onComplete(callback) {
		this.completeCallbacks.push(callback);
	}
	/**
	* Register an error callback: (actionIndex, error).
	* Called when a dispatch throws. Replay continues past the error.
	* After MAX_CONSECUTIVE_ERRORS consecutive failures, engine auto-pauses (R7).
	*/
	onError(callback) {
		this.errorCallbacks.push(callback);
	}
	/** Update playback speed (takes effect on next delay calculation) */
	setSpeed(factor) {
		if (!Number.isFinite(factor) || factor <= 0) throw new RangeError(`Speed factor must be a positive finite number, got ${factor}`);
		this.speedFactor = factor;
	}
	/**
	* Start replaying actions with timed delays.
	*
	* @param actions - Array of Redux actions to dispatch
	* @param store - RecorderStore to dispatch into
	* @param speedFactor - Initial playback speed (1 = real-time)
	* @returns Promise that resolves when replay completes or is paused
	*/
	play(actions, store, speedFactor) {
		this.abortController?.abort();
		this.actions = actions;
		this.store = store;
		this.speedFactor = speedFactor;
		this.currentIndex = 0;
		this.state = "playing";
		this.abortController = new AbortController();
		this.currentPlayPromise = this.runLoop(this.abortController.signal);
		return this.currentPlayPromise;
	}
	/** Pause the replay. Can be resumed later with resume(). */
	pause() {
		if (this.state !== "playing") return;
		this.state = "paused";
		this.abortController?.abort();
		this.abortController = null;
	}
	/**
	* Resume from the current action index after a pause.
	* @returns Promise that resolves when replay completes or is paused again
	*/
	resume() {
		if (this.state !== "paused" || !this.store) return Promise.resolve();
		this.state = "playing";
		this.abortController = new AbortController();
		this.currentPlayPromise = this.runLoop(this.abortController.signal);
		return this.currentPlayPromise;
	}
	/** Stop playback and reset to idle. */
	dispose() {
		this.abortController?.abort();
		this.abortController = null;
		this.state = "idle";
		this.currentIndex = 0;
		this.actions = [];
		this.store = null;
		this.currentPlayPromise = null;
		this.progressCallbacks = [];
		this.completeCallbacks = [];
		this.errorCallbacks = [];
	}
	async runLoop(signal) {
		const { actions, store } = this;
		if (!store) return;
		const total = actions.length;
		if (total === 0) {
			this.state = "completed";
			this.notifyComplete();
			return;
		}
		let consecutiveErrors = 0;
		while (this.currentIndex < total) {
			if (signal.aborted) return;
			const action = actions[this.currentIndex];
			try {
				store.dispatch(action);
				consecutiveErrors = 0;
			} catch (err) {
				consecutiveErrors++;
				const error = err instanceof Error ? err : new Error(String(err));
				this.notifyError(this.currentIndex + 1, error);
				if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
					this.state = "paused";
					this.abortController?.abort();
					this.abortController = null;
					return;
				}
			}
			this.currentIndex++;
			this.notifyProgress(this.currentIndex, total);
			if (this.currentIndex >= total) {
				this.state = "completed";
				this.notifyComplete();
				return;
			}
			const delay = computeInterActionDelay(extractActionTimestamp(action), extractActionTimestamp(actions[this.currentIndex]), this.speedFactor);
			if (delay > 0) {
				if (await this.abortableDelay(delay, signal)) return;
			}
		}
	}
	/**
	* Promise-based delay that resolves on timeout or rejects on abort.
	* Returns true if aborted, false if delay completed normally.
	*/
	abortableDelay(ms, signal) {
		return new Promise((resolve) => {
			if (signal.aborted) {
				resolve(true);
				return;
			}
			const timer = setTimeout(() => {
				signal.removeEventListener("abort", onAbort);
				resolve(false);
			}, ms);
			const onAbort = () => {
				clearTimeout(timer);
				resolve(true);
			};
			signal.addEventListener("abort", onAbort, { once: true });
		});
	}
	notifyProgress(current, total) {
		for (const cb of this.progressCallbacks) cb(current, total);
	}
	notifyComplete() {
		for (const cb of this.completeCallbacks) cb();
	}
	notifyError(actionIndex, error) {
		for (const cb of this.errorCallbacks) cb(actionIndex, error);
	}
};
//#endregion
export { DEFAULT_MAX_DELAY_MS, ReplayEngine, computeInterActionDelay, extractActionTimestamp };
